import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import dayjs from "dayjs";
import axios from "axios";

const createEmptyEntry = () => ({
  wh_type: "",
  wh_position: "",
  wh_comm_vehicle: "",
  wh_comm_vehicle_types: [],
  wh_name: "",
  wh_address1: "",
  wh_address2: "",
  wh_city: "",
  wh_province: "",
  wh_country: "",
  wh_postcode: "",
  wh_confname: "",
  wh_conlname: "",
  wh_contact_position: "",
  wh_phone: "",
  wh_email: "",
  wh_datefrom: "",
  wh_dateto: "",
  wh_status: "No",
  wh_terminated: "",
  wh_reason: "",
  wh_graduated: "",
  wh_gpa: "",
  wh_hours_of_instruction: "",
  wh_branch_service: "",
  wh_military_rank: "",
  wh_dot_safety_sensitive: "",
  wh_fmcsa: "",
  wh_skills_border: false,
  wh_skills_logbooks: false,
  wh_skills_fmcr: false,
  wh_skills_hazmat: false,
});

const trimValue = (value) =>
  typeof value === "string" ? value.trim() : value;

const getInitialEntry = (form) => {
  if (form.editingWorkHistory) {
    return { ...createEmptyEntry(), ...form.editingWorkHistory };
  }
  if (form.workHistoryDraft) {
    return { ...createEmptyEntry(), ...form.workHistoryDraft };
  }
  return createEmptyEntry();
};

const Step8WorkHistoryPage = ({ form, setForm, nextStep, prevStep }) => {
  const [entry, setEntry] = useState(() => getInitialEntry(form));
  const [stillWorking, setStillWorking] = useState(
    form.editingWorkHistory
      ? form.editingWorkHistory.wh_status === "Yes"
      : Boolean(form.workHistoryStillWorking),
  );
  const [equipOptions, setEquipOptions] = useState([]);
  const [eduOptions, setEduOptions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setEntry(getInitialEntry(form));
    setStillWorking(
      form.editingWorkHistory
        ? form.editingWorkHistory.wh_status === "Yes"
        : Boolean(form.workHistoryStillWorking),
    );
    setErrors({});
  }, [form.editingIndex, form.editingWorkHistory, form.workHistoryStillWorking]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      workHistoryDraft: entry,
      workHistoryStillWorking: stillWorking,
    }));
  }, [entry, setForm, stillWorking]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/dropdowns/highest-edu`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setEduOptions(list);
      })
      .catch(() => setEduOptions([]));
  }, []);

  useEffect(() => {
    if (entry.wh_comm_vehicle === "Yes") {
      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}/api/dropdowns/equip-operated`,
        )
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          setEquipOptions(list);
        })
        .catch(() => setEquipOptions([]));
      return;
    }

    setEquipOptions([]);
  }, [entry.wh_comm_vehicle]);

  useEffect(() => {
    if (entry.wh_comm_vehicle !== "Yes" && entry.wh_comm_vehicle_types.length) {
      setEntry((prev) => ({
        ...prev,
        wh_comm_vehicle_types: [],
      }));
    }
  }, [entry.wh_comm_vehicle, entry.wh_comm_vehicle_types]);

  const validateEntry = () => {
    const nextErrors = {};

    if (!trimValue(entry.wh_datefrom)) {
      nextErrors.wh_datefrom = "From date is required.";
    }

    if (!stillWorking && !trimValue(entry.wh_dateto)) {
      nextErrors.wh_dateto = "To date is required.";
    }

    if (
      entry.wh_datefrom &&
      entry.wh_dateto &&
      !stillWorking &&
      dayjs(entry.wh_dateto).isBefore(dayjs(entry.wh_datefrom), "day")
    ) {
      nextErrors.wh_dateto = "To date must be on or after From date.";
    }

    if (
      entry.wh_type === "Working" ||
      entry.wh_type === "Driving School" ||
      entry.wh_type === "School"
    ) {
      if (!trimValue(entry.wh_name)) {
        nextErrors.wh_name = "Company / School name is required.";
      }
      if (!trimValue(entry.wh_country)) {
        nextErrors.wh_country = "Country is required.";
      }
    }

    if (entry.wh_type === "Working") {
      if (!trimValue(entry.wh_position)) {
        nextErrors.wh_position = "Position is required.";
      }
      if (!trimValue(entry.wh_comm_vehicle)) {
        nextErrors.wh_comm_vehicle =
          "Commercial vehicle selection is required.";
      }
      if (
        entry.wh_comm_vehicle === "Yes" &&
        entry.wh_comm_vehicle_types.length === 0
      ) {
        nextErrors.wh_comm_vehicle_types =
          "Select at least one commercial vehicle type.";
      }
      if (!trimValue(entry.wh_confname)) {
        nextErrors.wh_confname = "Contact first name is required.";
      }
      if (!trimValue(entry.wh_contact_position)) {
        nextErrors.wh_contact_position = "Contact position is required.";
      }
      if (!trimValue(entry.wh_phone)) {
        nextErrors.wh_phone = "Phone is required.";
      }
      if (!trimValue(entry.wh_email)) {
        nextErrors.wh_email = "Email is required.";
      }
      if (!trimValue(entry.wh_terminated)) {
        nextErrors.wh_terminated = "Terminated selection is required.";
      }
      if (!trimValue(entry.wh_reason)) {
        nextErrors.wh_reason = "Reason for leaving is required.";
      }
    }

    if (entry.wh_type === "School" && !trimValue(entry.wh_position)) {
      nextErrors.wh_position = "Level of education is required.";
    }

    return nextErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEntry((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEntry((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleTypeSelect = (type) => {
    setEntry((prev) => ({
      ...prev,
      wh_type: type,
    }));
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateEntry();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const newEntry = {
      ...entry,
      wh_status: stillWorking ? "Yes" : "No",
      wh_dateto: stillWorking ? null : entry.wh_dateto,
      wh_safety_sensitive: entry.wh_dot_safety_sensitive || null,
      wh_transport_regulation: entry.wh_fmcsa || null,
      wh_skills: {
        border_crossing: !!entry.wh_skills_border,
        log_books: !!entry.wh_skills_logbooks,
        fmcr: !!entry.wh_skills_fmcr,
        hazmat: !!entry.wh_skills_hazmat,
      },
      wh_comm_vehicle_types: entry.wh_comm_vehicle_types || [],
    };

    setForm((prevForm) => {
      const updated = [...(prevForm.workHistories || [])];
      if (form.editingIndex != null) {
        updated[form.editingIndex] = newEntry;
      } else {
        updated.push(newEntry);
      }
      return {
        ...prevForm,
        workHistories: updated,
        workHistoryDraft: null,
        workHistoryStillWorking: false,
        editingWorkHistory: null,
        editingIndex: null,
      };
    });

    nextStep();
  };

  if (!entry.wh_type) {
    const types = [
      "Working",
      "Unemployed",
      "School",
      "Driving School",
      "Military",
    ];

    return (
      <Container className="text-center mt-5">
        <h3>Select Work History Type</h3>
        <div className="d-grid gap-3 mt-4 mb-3">
          {types.map((type) => (
            <Button
              key={type}
              variant="outline-primary"
              size="lg"
              onClick={() => handleTypeSelect(type)}
            >
              {type}
            </Button>
          ))}
        </div>
        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={prevStep}>
            Back
          </Button>
          <Button variant="primary" onClick={nextStep}>
            Next
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <h5 className="mb-4">Add {entry.wh_type} History</h5>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  From <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="wh_datefrom"
                  value={entry.wh_datefrom}
                  onChange={handleChange}
                  isInvalid={!!errors.wh_datefrom}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.wh_datefrom}
                </Form.Control.Feedback>
              </Col>
              <Col md={6}>
                <Form.Label>
                  To <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="wh_dateto"
                  value={entry.wh_dateto}
                  onChange={handleChange}
                  disabled={stillWorking}
                  isInvalid={!!errors.wh_dateto}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.wh_dateto}
                </Form.Control.Feedback>
              </Col>
            </Row>
            <Form.Check
              className="mb-4"
              type="checkbox"
              label="Still Active?"
              checked={stillWorking}
              onChange={(e) => {
                const checked = e.target.checked;
                setStillWorking(checked);
                setErrors((prev) => ({ ...prev, wh_dateto: undefined }));
              }}
            />

            {entry.wh_type === "Working" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Position <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="wh_position"
                    value={entry.wh_position}
                    onChange={handleChange}
                    isInvalid={!!errors.wh_position}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.wh_position}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Did you operate a commercial vehicle?{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="wh_comm_vehicle"
                    value={entry.wh_comm_vehicle}
                    onChange={handleChange}
                    isInvalid={!!errors.wh_comm_vehicle}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.wh_comm_vehicle}
                  </Form.Control.Feedback>
                </Form.Group>

                {entry.wh_comm_vehicle === "Yes" && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      What type of commercial vehicle?{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      isMulti
                      options={equipOptions.map((opt) => ({
                        value: opt.eo_name,
                        label: opt.eo_name,
                      }))}
                      value={entry.wh_comm_vehicle_types.map((val) => ({
                        value: val,
                        label: val,
                      }))}
                      onChange={(selected) => {
                        setEntry((prev) => ({
                          ...prev,
                          wh_comm_vehicle_types: (selected || []).map(
                            (item) => item.value,
                          ),
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          wh_comm_vehicle_types: undefined,
                        }));
                      }}
                    />
                    {errors.wh_comm_vehicle_types && (
                      <div className="text-danger small mt-1">
                        {errors.wh_comm_vehicle_types}
                      </div>
                    )}
                  </Form.Group>
                )}
              </>
            )}

            {(entry.wh_type === "Working" ||
              entry.wh_type === "Driving School" ||
              entry.wh_type === "School") && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Comapny / School Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="wh_name"
                    value={entry.wh_name}
                    onChange={handleChange}
                    isInvalid={!!errors.wh_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.wh_name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Control
                      placeholder="Address 1"
                      name="wh_address1"
                      value={entry.wh_address1}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Control
                      placeholder="Address 2"
                      name="wh_address2"
                      value={entry.wh_address2}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={6}>
                    <Form.Control
                      placeholder="City"
                      name="wh_city"
                      value={entry.wh_city}
                      onChange={handleChange}
                    />
                  </Col>

                  <Col xs={6}>
                    <Form.Control
                      placeholder="Province"
                      name="wh_province"
                      value={entry.wh_province}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={6}>
                    <Form.Control
                      placeholder="Country"
                      name="wh_country"
                      value={entry.wh_country}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_country}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_country}
                    </Form.Control.Feedback>
                  </Col>

                  <Col xs={6}>
                    <Form.Control
                      placeholder="ZIP Code"
                      name="wh_postcode"
                      value={entry.wh_postcode}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>
              </>
            )}

            {entry.wh_type === "Working" && (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>
                      Contact First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="wh_confname"
                      value={entry.wh_confname}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_confname}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_confname}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Contact Last Name</Form.Label>
                    <Form.Control
                      name="wh_conlname"
                      value={entry.wh_conlname}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>
                      Contact Position <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="wh_contact_position"
                      value={entry.wh_contact_position}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_contact_position}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_contact_position}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>
                      Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="wh_phone"
                      value={entry.wh_phone}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_phone}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="wh_email"
                      value={entry.wh_email}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_email}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>
                      Terminated <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="wh_terminated"
                      value={entry.wh_terminated}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_terminated}
                    >
                      <option value="">-- Select --</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_terminated}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Reason for Leaving <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="wh_reason"
                      value={entry.wh_reason}
                      onChange={handleChange}
                      isInvalid={!!errors.wh_reason}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.wh_reason}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </>
            )}

            {entry.wh_type === "Unemployed" && (
              <Form.Group className="mb-3">
                <Form.Label>Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  name="wh_reason"
                  value={entry.wh_reason}
                  onChange={handleChange}
                />
              </Form.Group>
            )}

            {entry.wh_type === "School" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Level of Education <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="wh_position"
                    value={entry.wh_position}
                    onChange={handleChange}
                    isInvalid={!!errors.wh_position}
                  >
                    <option value="">Select Level</option>
                    {entry.wh_position &&
                      !eduOptions.some((edu) => edu.he_name === entry.wh_position) && (
                        <option value={entry.wh_position}>
                          {entry.wh_position}
                        </option>
                      )}
                    {eduOptions.map((edu) => (
                      <option key={edu.he_id} value={edu.he_name}>
                        {edu.he_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.wh_position}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Graduated? <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="wh_graduated"
                    value={entry.wh_graduated}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </>
            )}

            {entry.wh_type === "Driving School" && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>
                      Did you graduate? <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="wh_graduated"
                      value={entry.wh_graduated}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>GPA</Form.Label>
                    <Form.Control
                      name="wh_gpa"
                      value={entry.wh_gpa || ""}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>
                      Hours of Instruction{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="wh_hours_of_instruction"
                      value={entry.wh_hours_of_instruction}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="50">50</option>
                      <option value="80">80</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Safety Sensitive Functions (DOT)?{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="wh_dot_safety_sensitive"
                      value={entry.wh_dot_safety_sensitive}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Label>
                      Transport Canada / FMCSR?{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="wh_fmcsa"
                      value={entry.wh_fmcsa}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col>
                    <Form.Label>Skills Trained</Form.Label>
                    <div className="form-check">
                      <Form.Check
                        type="checkbox"
                        label="Border Crossing"
                        name="wh_skills_border"
                        checked={entry.wh_skills_border || false}
                        onChange={handleCheckboxChange}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Log Books"
                        name="wh_skills_logbooks"
                        checked={entry.wh_skills_logbooks || false}
                        onChange={handleCheckboxChange}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Federal Motor Carrier Regulations"
                        name="wh_skills_fmcr"
                        checked={entry.wh_skills_fmcr || false}
                        onChange={handleCheckboxChange}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Hazardous Materials"
                        name="wh_skills_hazmat"
                        checked={entry.wh_skills_hazmat || false}
                        onChange={handleCheckboxChange}
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {entry.wh_type === "Military" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Branch of Service <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="wh_branch_service"
                    value={entry.wh_branch_service}
                    onChange={handleChange}
                  >
                    <option value="">Select Branch</option>
                    <option value="Army">Army</option>
                    <option value="Air Force">Air Force</option>
                    <option value="Navy">Navy</option>
                    <option value="Marines">Marines</option>
                    <option value="Coast Guard">Coast Guard</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Military Rank</Form.Label>
                  <Form.Control
                    name="wh_military_rank"
                    value={entry.wh_military_rank}
                    onChange={handleChange}
                  />
                </Form.Group>
              </>
            )}

            <div className="d-flex justify-content-between mt-4">
              <Button variant="secondary" onClick={prevStep}>
                Back
              </Button>
              <Button type="submit" variant="primary">
                Next
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Step8WorkHistoryPage;
